import React, { useState } from "react"
import { Box, Chip, Divider, makeStyles, Typography } from "@material-ui/core"
import { NavLink } from "react-router-dom"
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons"
import { PaperBox, RowBox } from "../Boxes"
import { LinearProgress } from "../LinearProgress"
import { USER_TRAINING_MODULE_STATUS } from "../../data"
import { Icon } from ".."
import TimeAgo from "../TimeAgo"
import { toId } from "../../utils"

const useStyles = makeStyles((theme) => ({
  paper: {
    marginBottom: theme.spacing(3),
    cursor: ({ completed }) => (completed ? "pointer" : "default"),
  },
  title: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: "500",
    color: ({ status }) =>
      status > USER_TRAINING_MODULE_STATUS.INCOMPLETE ? theme.palette.text.main : theme.palette.text.faint,
  },
  progress: {
    width: 100,
  },
  expander: {
    cursor: "pointer",
  },
  completedChip: {
    fontWeight: "600",
    color: "white",
    backgroundColor: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
  caption: {
    fontSize: 12,
    lineHeight: "16px",
    color: theme.palette.text.light,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  iconInProgress: {
    fontSize: 18,
    fill: theme.palette.text.light,
  },
  iconCompleted: {
    fontSize: 18,
    fill: theme.palette.success.main,
  },
  iconNavigate: {
    fontSize: 18,
    fill: theme.palette.text.light,
  },
  activeModule: {
    color: theme.palette.text.main,
    "&:hover": {
      backgroundColor: theme.palette.background.shaded,
    },
  },
  disabledModule: {
    color: theme.palette.text.faint,
  },
}))

const TrainingKnowledge = ({ activeKnowledge, moduleKnowledge }) => {
  const {
    knowledge: { id, title },
    completedAt,
  } = moduleKnowledge

  const classes = useStyles()
  const completed = Boolean(completedAt)
  const disabled = !completed && toId(moduleKnowledge.knowledge) !== toId(activeKnowledge)
  const className = disabled ? classes.disabledModule : classes.activeModule

  return (
    <RowBox
      component={!disabled ? NavLink : null}
      to={`/knowledge/article/${id}`}
      pl={2}
      pr={1}
      py={1}
      className={className}
      data-cy="TrainingKnowledge"
    >
      {!disabled && (
        <RowBox mr={1}>
          {!completed && <Icon name="forward" className={classes.iconInProgress} />}
          {completed && <Icon name="training-module-complete" className={classes.iconCompleted} />}
        </RowBox>
      )}
      <Box>{title}</Box>
      {!disabled && (
        <RowBox ml="auto">
          {!completed && (
            <>
              <Box className={classes.caption}>View training</Box>
              <Icon name="right" className={classes.iconNavigate} />
            </>
          )}
          {completed && (
            <Box className={classes.caption} pr={1}>
              <TimeAgo date={completedAt} />
            </Box>
          )}
        </RowBox>
      )}
    </RowBox>
  )
}

const TrainingModule = ({ activeKnowledge, name, knowledges, status, percentComplete, completedAt }) => {
  const completed = status === USER_TRAINING_MODULE_STATUS.COMPLETE
  const active = status === USER_TRAINING_MODULE_STATUS.IN_PROGRESS
  const disabled = status === USER_TRAINING_MODULE_STATUS.INCOMPLETE
  const classes = useStyles({ status, completed })
  const [expanded, setExpanded] = useState(false)

  const handleToggleExpanded = () => {
    if (!completed) {
      return
    }
    setExpanded(!expanded)
  }

  return (
    <PaperBox className={classes.paper} completed={completed} onClick={handleToggleExpanded}>
      <Box p={2}>
        <RowBox>
          <Typography variant="h2" className={classes.title}>
            {name}
          </Typography>
          {!disabled && (
            <>
              {!completed && (
                <RowBox ml="auto">
                  <Box mr={1} className={classes.caption}>
                    {knowledges.filter((knowledge) => knowledge.completedAt).length} of {knowledges.length}
                  </Box>
                  <Box className={classes.progress}>
                    <LinearProgress value={percentComplete} />
                  </Box>
                </RowBox>
              )}
              {completed && (
                <RowBox ml="auto">
                  <Box className={classes.caption} pr={1}>
                    <TimeAgo date={completedAt} />
                  </Box>
                  <Chip label="Complete" size="small" className={classes.completedChip} />
                  {!expanded && <KeyboardArrowDown className={classes.expander} />}
                  {expanded && <KeyboardArrowUp className={classes.expander} />}
                </RowBox>
              )}
            </>
          )}
        </RowBox>
      </Box>
      {(active || expanded) && (
        <>
          <Divider />
          <Box>
            {knowledges.map((moduleKnowledge, index) => (
              <Box key={index}>
                <TrainingKnowledge activeKnowledge={activeKnowledge} moduleKnowledge={moduleKnowledge} />
                <Divider />
              </Box>
            ))}
          </Box>
        </>
      )}
    </PaperBox>
  )
}

const TrainingCurriculum = ({ training }) => (
  <>
    {training.status.modules.map((module) => (
      <TrainingModule key={toId(module)} activeKnowledge={training.status.activeKnowledge} {...module} />
    ))}
  </>
)

export { TrainingCurriculum }
